'use client';
import { Dialog, Portal } from '@ark-ui/react';
import { useCallback } from 'react';
import { memo } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '@app/_components/button';
import { Paragraph } from '@styled-system/jsx';
import { styled } from '@styled-system/jsx';

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onChangeType: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeMessage: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  setCaptchaToken: (token?: string) => void;
  submitDisabled: boolean;
};

const _StoryReportModal = ({
  isOpen,
  onClose,
  onChangeType,
  onChangeMessage,
  setCaptchaToken,
  onSubmit,
  submitDisabled,
}: ModalProps) => {
  const handleOnOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={handleOnOpenChange}
    >
      <Portal>
        <Backdrop />
        <Positioner>
          <Content>
            <form role="presentation" autoComplete="off" onSubmit={onSubmit}>
              <Paragraph
                lineHeight="24px"
                fontSize="size_20"
                fontWeight="700"
                mb="20px"
              >
                作品の報告
              </Paragraph>
              <Label>報告理由</Label>
              <Select
                name="type"
                id="type"
                onChange={onChangeType}
                width="100%"
                data-testid="report-type"
              >
                <option value="5">センシティブな内容</option>
                <option value="3">出会い目的・連絡先交換・個人情報</option>
                <option value="2">誹謗中傷・嫌がらせ・いじめ等</option>
                <option value="1">自傷行為に関連する内容</option>
                <option value="6">スパム・商業目的のコンテンツ</option>
                <option value="7">その他（顔出し他）</option>
              </Select>

              <Label htmlFor="message">具体的な内容</Label>
              <Textarea
                name="message"
                placeholder="具体的な内容をご記入ください。"
                onChange={onChangeMessage}
                data-testid="report-message"
              ></Textarea>

              <ReCAPTCHA
                sitekey={
                  process.env['NEXT_PUBLIC_RECAPTCHA_SITE_KEY_FOR_REPORT']!
                }
                hl="ja"
                datatype="image"
                onChange={(token) => setCaptchaToken(token ?? undefined)}
                onExpired={() => setCaptchaToken(undefined)}
              />

              <Button
                size="small"
                width="100%"
                maxWidth="360px"
                height="40px"
                backgroundColor="tint"
                color="white"
                fontSize="size_16"
                fontWeight="bold"
                margin="32px auto 16px auto"
                disabled={submitDisabled}
                data-testid="report-submit"
                _focus={{
                  outline: 0,
                }}
              >
                送信
              </Button>
              <Paragraph
                fontSize="size_11"
                width="100%"
                maxWidth="360px"
                margin="0 auto"
              >
                内容が不明瞭な場合は対応できない場合がありますので、詳しい内容をご記入お願いします。
              </Paragraph>
            </form>
          </Content>
        </Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const Backdrop = styled(Dialog.Backdrop, {
  base: {
    backdropFilter: 'blur(4px)',
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: '0',
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: 'modal_backdrop',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Positioner = styled(Dialog.Positioner, {
  base: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: {
      base: 0,
      lg: 24,
    },
    bottom: {
      base: 0,
      lg: 24,
    },
    left: {
      base: 0,
      lg: undefined,
    },
    right: {
      base: 0,
      lg: undefined,
    },
    margin: {
      base: 0,
      lg: 'auto',
    },
    height: {
      base: '100dvh',
      lg: '300px',
    },
    width: {
      base: '100dvw',
      lg: '680px',
    },
    zIndex: 'teller_modal',
  },
});

const Content = styled(Dialog.Content, {
  base: {
    background: 'white',
    boxShadow: 'lg',
    textAlign: 'left',
    padding: '40px',
    borderRadius: {
      base: '8px',
    },
    height: 'auto',
    width: '90%',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Select = styled('select', {
  base: {
    border: '1px solid #E2E8F0',
    width: '100%',
    height: '2.5rem',
    fontSize: 'size_16',
    borderRadius: '6px',
    padding: '5px',
    _focus: {
      outline: 0,
    },
  },
});

const Textarea = styled('textarea', {
  base: {
    border: '1px solid #E2E8F0',
    fontSize: 'size_16',
    borderRadius: '6px',
    padding: '5px',
    width: '100%',
    height: '160px',
    resize: 'none',
    marginBottom: '16px',
    _focus: {
      outline: 0,
    },
  },
});

const Label = styled('label', {
  base: {
    fontSize: 'size_12',
    color: 'accent.700',
    display: 'block',
    marginTop: '22px',
    marginBottom: '4px',
  },
});

export const StoryReportModal = memo(_StoryReportModal);
